<template lang="pug">
  .account(@mouseleave="hover(false)")
    rogue-link.status(v-if="!loggedIn" to="/login" :aria-label="__('Log In')" @click="login($event)")
      Icon(name="guest" fill="white")
      .text {{__('Log In')}}
    .status(@mouseover="hover(true)" @click="hover(true)" v-else)
      Icon(name="logged-in" fill="white")
      .text {{__('My Account')}}
      Tooltip(:manual="true" placement="bottom" ref="tt" :offset="[0,0]" theme="custom" v-if="loggedIn")
        .tip-spot
        template(#tooltip)
          .dropdown
            rogue-link(to="/account/dashboard") {{__('My Account')}}
            rogue-link(v-if="$rogue.site.code === 'USA'" to="/zeus-builder") {{__('ZEUS Gym Builder')}}
            rogue-link(to="/account/orders") {{__('Order History')}}
            rogue-link(to="/account/programming") {{__('Iron Game Programming')}}
            rogue-link(to="/account/my-lists") {{__('My Wishlist')}}
            rogue-link(to="/account/settings") {{__('Account Settings')}}
            rogue-link(@click="logout()") {{__('Log Out')}}
    rogue-link.cart(
      to="/cart"
      class="cart-icon"
      @click="showSideCart"
      tabindex="-1"
      aria-label="Show Cart"
    )
      Icon(name="cart" fill="white")
      .text {{cartQty}}
</template>
<script setup lang="ts">
import { computed, onMounted, ref, useTemplateRef } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useCustomerStore } from '@/js/stores/customerStore'
import { useListStore } from '@/js/stores/listStore'
import { useUIStore } from '@/js/stores/uiStore'
import { useRogue } from '@/js/services/Rogue'

const customerStore = useCustomerStore()
const listStore = useListStore()
const uiStore = useUIStore()
const route = useRoute()
const router = useRouter()
const rogue = useRogue()

interface Tooltip {
  hover(val: boolean): void;
}

const toolTipRef = useTemplateRef<Tooltip>('tt')

const loggedIn = computed(() => mounted.value && customerStore.loggedIn)
const cartQty = computed(() => listStore.cartQty)
const isFunneled = computed(() => route.meta.isFunneled)

const isEventPage = computed(() => {
  const eventPaths = [
    '/invitational',
    '/arnold-strongman-classic',
    '/events/invitational',
    '/events/arnold-strongman-classic',
  ]

  return eventPaths.some(p => route.fullPath.startsWith(p))
})

const mounted = ref(false)

onMounted(() => {
  mounted.value = true
})

let closeTimeout: NodeJS.Timeout | undefined = undefined

async function logout() {
  await customerStore.logout(rogue)
  clearTimeout(closeTimeout)

  if (route.fullPath.indexOf('/account') === 0) {
    router.push('/').catch((err) => {
      rogue.log.error('Error changing to login in', err)
    })
  }
}

function showSideCart(event: Event) {
  if (!isFunneled.value) {
    event.preventDefault()
    uiStore.showOverlay('side-cart', rogue)
  }
}

function hover(val: boolean) {
  if (!toolTipRef.value) {
    return
  }
  if (val) {
    clearTimeout(closeTimeout)
    if (toolTipRef.value) {
      toolTipRef.value.hover(true)
    }
  } else {
    closeTimeout = setTimeout(() => {
      if (toolTipRef.value) {
        toolTipRef.value.hover(false)
      }
    }, 100)
  }
}

function login(event: Event) {
  if (isEventPage.value) {
    event.preventDefault()
    event.stopPropagation()
    uiStore.showOverlay('regularLogin', rogue)
  }
}
</script>
<style lang="scss" scoped>
.account {
  display: flex;
  height: 36px;
  @include all-desktop() {
    width: 170px;
    justify-content: flex-end;
  }
}

.status, .cart {
  display: flex;
  align-items: center;
  cursor: pointer;
  position: relative;
  text-decoration: none;
}

.ttip {
  position: absolute;
  left: 12px;
  bottom: 0;
}

.tip-spot {
  width: 24px;
  position: absolute;
  left: 0;
  bottom: 0;
}

.cart {
  margin-left: $space-s;
}

.text {
  margin-left: 4px;
  font-size: $size-xs;
  text-decoration: none;
  color: $color-white;
}

.status .text {
  display: none;
  @include all-desktop() {
    display: block;
  }
}

.dropdown a {
  padding: $space-xs;
  text-decoration: none;
  color: $color-black;
  font-size: $size-xxs;
  white-space: nowrap;
  display: block;
  background-color: $color-white;

  &:hover {
    background-color: $color-gray-light;
  }

  &:not(:last-child) {
    border-bottom: 1px solid #D2D2D2;
  }
}

.icon {
  width: 20px;
  height: 20px;
}
</style>
